import React, { useEffect, useState } from 'react';
import { Check, Loader2, RefreshCw, CalendarClock } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { retainers } from '../../api';

const zar = (n) => `R ${Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Cell = ({ cell, pending, onToggle, busy, testid }) => {
  if (!cell) return <td className="px-3 py-3 text-center text-xs text-stone-300" data-testid={testid}>{pending ? 'due 25th' : '—'}</td>;
  const paid = cell.status === 'paid';
  const partial = cell.status === 'partial';
  return (
    <td className={`px-3 py-3 text-center ${paid ? 'bg-emerald-50/60' : cell.overdue ? 'bg-red-50/60' : ''}`} data-testid={testid}>
      <label className="inline-flex flex-col items-center gap-1 cursor-pointer">
        {busy ? <Loader2 size={16} className="animate-spin text-stone-400"/> : <Checkbox checked={paid} onCheckedChange={(c) => onToggle(cell, !!c)} data-testid={`${testid}-checkbox`}/>}
        <span className={`text-[11px] tabular-nums ${paid ? 'text-emerald-800' : cell.overdue ? 'text-red-700 font-semibold' : 'text-stone-600'}`}>
          {paid ? 'Paid' : partial ? `${zar(cell.balance)} owing` : zar(cell.total)}
        </span>
      </label>
    </td>
  );
};

export const RetainerTracker = ({ toast }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => retainers.list().then(setData).catch(() => setData({ months: [], rows: [], totals: {} }));
  useEffect(() => { load(); }, []);

  const toggle = (cell, paid) => {
    if (!paid && !window.confirm(`Mark ${cell.number} as unpaid? Recorded payments on it will be removed.`)) return;
    setBusy(cell.id);
    retainers.markPaid(cell.id, paid).then(() => { toast({ title: paid ? 'Marked as paid' : 'Marked as unpaid', description: cell.number }); return load(); })
      .catch((e) => toast({ title: 'Could not update', description: e?.response?.data?.detail || 'Try again.', variant: 'destructive' }))
      .finally(() => setBusy(null));
  };
  const runNow = () => retainers.run().then((r) => { toast({ title: r.created ? `${r.created} retainer invoice(s) raised` : 'All retainer invoices are up to date' }); load(); });

  if (!data) return <div className="p-16 flex justify-center"><Loader2 className="animate-spin text-stone-400"/></div>;
  return (
    <div className="rounded-xl bg-white border border-stone-200 overflow-hidden" data-testid="retainer-tracker">
      <div className="p-5 border-b border-stone-100 flex flex-wrap items-center gap-4 justify-between">
        <div>
          <div className="font-semibold text-stone-900 flex items-center gap-2"><CalendarClock size={16} className="text-emerald-800"/> Monthly retainers</div>
          <div className="text-xs text-stone-500 mt-0.5">A retainer invoice is raised for every client on the {data.invoiceDay}th of each month. Tick a month when the fee is paid. Unpaid months and other unpaid invoices show as owing on the client portal.</div>
        </div>
        <div className="flex items-center gap-5">
          <div className="text-right"><div className="text-[11px] uppercase tracking-wider text-stone-500">Retainers / month</div><div className="font-semibold tabular-nums" data-testid="retainer-mrr">{zar(data.totals.mrr)}</div></div>
          <div className="text-right"><div className="text-[11px] uppercase tracking-wider text-stone-500">Total owing</div><div className="font-semibold tabular-nums text-red-700" data-testid="retainer-total-owing">{zar(data.totals.owing)}</div></div>
          <Button size="sm" variant="outline" onClick={runNow} data-testid="retainer-run-btn"><RefreshCw size={13} className="mr-1.5"/> Check now</Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="retainer-table">
          <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-5 py-3 font-medium sticky left-0 bg-stone-50">Client</th>
              <th className="text-right px-3 py-3 font-medium">Fee</th>
              {data.months.map((m) => <th key={m.key} className="text-center px-3 py-3 font-medium whitespace-nowrap">{m.label.replace(' 20', " '")}</th>)}
              <th className="text-right px-3 py-3 font-medium">Other invoices</th>
              <th className="text-right px-5 py-3 font-medium">Owing</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {data.rows.length === 0 && <tr><td colSpan={data.months.length + 4} className="px-5 py-10 text-center text-stone-500">Add a monthly fee to your clients to start retainer invoicing.</td></tr>}
            {data.rows.map((r) => (
              <tr key={r.clientId} data-testid={`retainer-row-${r.clientId}`}>
                <td className="px-5 py-3 font-medium text-stone-900 sticky left-0 bg-white whitespace-nowrap">{r.clientName}</td>
                <td className="px-3 py-3 text-right tabular-nums text-stone-700">{zar(r.retainer)}</td>
                {data.months.map((m) => <Cell key={m.key} cell={r.cells[m.key]} pending={m.pending} busy={busy === r.cells[m.key]?.id} onToggle={toggle} testid={`retainer-cell-${r.clientId}-${m.key}`}/>)}
                <td className="px-3 py-3 text-right tabular-nums text-stone-700" title={r.otherInvoices.map((i) => `${i.number}: ${zar(i.balance)}`).join('\n')} data-testid={`retainer-other-${r.clientId}`}>{r.otherOwing ? zar(r.otherOwing) : '—'}</td>
                <td className={`px-5 py-3 text-right tabular-nums font-semibold ${r.owing > 0 ? 'text-red-700' : 'text-emerald-800'}`} data-testid={`retainer-owing-${r.clientId}`}>{r.owing > 0 ? zar(r.owing) : <span className="inline-flex items-center gap-1"><Check size={13}/> Paid up</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
